import * as React from 'react';

import { INLINE_LINK, selectInline } from '@gitbook/doc-core';
import { useHiveClient } from '@gitbook/hive-react';
import { usePaletteHandle } from '@gitbook/react-uikit';
import { type Block, type Fragment, type Inline, TextRange } from '@gitbook/slate';
import type { EditorContainer } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import { Button, clipboard, toast } from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';

import {
    createInputContentRefPalette,
    type UIResolvedContentReference,
    useContentReferenceContext,
    useResolveContentReference,
} from '../../ContentReferences';
import { useEditLinkPopover } from '../../Link';
import { ContentEditorContext } from '../contexts';
import type { InlineLinkProps } from './useInlineLinkProp';

/**
 * Component to render a link node.
 */
export function EditLinkWrapper(props: {
    kind?: 'link' | 'reference';
    node: Inline;
    parent: Inline | Block | Fragment;
    editor: EditorContainer;
    resolved: UIResolvedContentReference;
    children: React.ReactElement;
    linkProps: InlineLinkProps | null;
}): React.ReactElement {
    const { kind = 'link', resolved, node, parent, editor, linkProps, children } = props;

    const contentReferenceContext = useContentReferenceContext();
    const hiveClient = useHiveClient();

    const { logAnalyticsEvent } = React.useContext(ContentEditorContext);

    const anchorRef = React.useRef<HTMLAnchorElement | HTMLSpanElement>(null);
    const paletteHandle = usePaletteHandle(anchorRef, {
        onClose: () => editor.change((change) => change.blur().focus()),
    });

    const onPressRemove = () => {
        editor.change((change) => {
            change.unwrapInlineByKey(node.key);
        });
    };

    const resolveContentRef = useResolveContentReference();

    const onPressEdit = () => {
        editor.change((change) => selectInline(change, node));

        paletteHandle.open(
            createInputContentRefPalette(
                (contentRef) => {
                    editor.change((change) => selectInline(change, node));

                    toast.catch(
                        () =>
                            resolveContentRef(contentRef).then(() => {
                                editor.change((change) => {
                                    change.setDataByKey(node.key, ['ref'], contentRef);
                                });

                                // Check if the ref is unresolved (e.g broken link), and if so set the fromValue to undefined
                                //
                                // This is to make sure we can distinguish updates to broken vs valid
                                // references in the analytics event
                                const isUnresolvedRef = Boolean(
                                    !resolved?.resolution || !resolved?.href
                                );
                                const fromValue = isUnresolvedRef
                                    ? undefined
                                    : node.data.get('ref').kind;

                                logAnalyticsEvent({
                                    type: 'edit_page_document_edit_block_property',
                                    blockType: INLINE_LINK,
                                    blockProperty: 'ref',
                                    fromValue,
                                    toValue: contentRef.kind,
                                });
                            }),
                        'Error while updating link'
                    );
                },
                {
                    ...contentReferenceContext,
                    document: editor.value.document,
                },
                hiveClient,
                {
                    search: {
                        initialValue:
                            typeof resolved.resolution?.target === 'string'
                                ? resolved.href
                                : undefined,
                    },
                }
            )
        );
    };

    const linkOutTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

    const { open: openPopover, close: closePopover } = useEditLinkPopover();

    const { selection } = editor.value;
    const isSelected = Boolean(
        TextRange.isTextRange(selection) &&
            selection.isExpanded &&
            selection.isFocused &&
            (selection.hasAnchorIn(node) ||
                selection.hasFocusIn(node) ||
                selection.isOnlyNodeWrapped(node, parent))
    );

    const open = useEventCallback((options?: { delay?: number }) => {
        if (linkOutTimeoutRef.current !== null) {
            clearTimeout(linkOutTimeoutRef.current);
        }

        const triggerOpen = () => {
            if (anchorRef.current) {
                openPopover({
                    anchor: anchorRef.current,
                    kind,
                    resolved,
                    onPressEdit,
                    onPressRemove,
                    linkProps,
                    open,
                    close,
                });
            }
        };

        if (!options?.delay) {
            // Defer opening to ensure the anchor is mounted
            setTimeout(triggerOpen, 0);
        } else {
            linkOutTimeoutRef.current = setTimeout(triggerOpen, options.delay);
        }
    });

    const close = useEventCallback((options?: { delay?: number }) => {
        if (paletteHandle.opened) {
            return;
        }

        const triggerClose = () => {
            linkOutTimeoutRef.current = null;
            closePopover();
        };

        if (!options?.delay) {
            triggerClose();
        } else {
            linkOutTimeoutRef.current = setTimeout(triggerClose, options.delay);
        }
    });

    React.useEffect(() => {
        if (isSelected) {
            open();
        } else if (!paletteHandle.opened) {
            close();
        }
    }, [isSelected, paletteHandle.opened]);

    // Clear timeout on unmount
    React.useEffect(() => {
        return () => {
            if (linkOutTimeoutRef.current !== null) {
                clearTimeout(linkOutTimeoutRef.current);
            }
        };
    }, []);

    return (
        <AnchorWrapper
            ref={anchorRef}
            onMouseOver={() => open({ delay: 150 })}
            onMouseOut={() => close({ delay: 150 })}
        >
            {children}
        </AnchorWrapper>
    );
}

/**
 * Create a wrapper for the anchor element to handle the onPress event.
 */
const AnchorWrapper = React.forwardRef(function AnchorWrapper(
    props: {
        children: React.ReactElement;
        onPress?: () => void;
    } & React.ComponentPropsWithoutRef<'a' | 'span'>,
    ref: React.ForwardedRef<HTMLAnchorElement | HTMLSpanElement>
) {
    const { children, onPress, ...rest } = props;
    const child = React.Children.only<
        React.ReactElement<React.ComponentPropsWithRef<'a' | 'span'>>
    >(props.children);
    return React.cloneElement(child, {
        ref,
        onClick: (event) => {
            child.props?.onClick?.(event);
            if (event.defaultPrevented) {
                onPress?.();
            }
        },
        ...rest,
    });
});

/**
 * Inner body of the popover to edit the link.
 */
export function EditPopoverBody(props: {
    kind?: 'link' | 'reference';
    resolved: UIResolvedContentReference;
    onPressEdit: () => void;
    onPressRemove: () => void;
    linkProps: InlineLinkProps | null;
}): React.ReactElement {
    const { kind = 'link', resolved, onPressEdit, onPressRemove, linkProps } = props;

    return (
        <div className="flex items-center rounded bg-tile-inverted p-1">
            <div className="flex max-w-56">
                <Button
                    label={resolved.title ?? actionsLabels[kind].open}
                    leadingIcon={Icon.ExternalLink}
                    size="small"
                    kind="blank"
                    inverted
                    href={linkProps?.href}
                    target={linkProps?.target}
                    onClick={
                        linkProps?.onClick
                            ? (event) => {
                                  if (linkProps?.onClick) {
                                      linkProps.onClick(event);
                                  }
                              }
                            : undefined
                    }
                    className="max-w-full flex-1 pr-2 pl-1"
                    preventFocusOnPress
                />
            </div>
            <Button
                label={actionsLabels[kind].edit}
                kind="blank"
                inverted
                iconOnly
                leadingIcon={Icon.Edit}
                size="small"
                onPress={onPressEdit}
                preventFocusOnPress
            />
            <Button
                label={actionsLabels[kind].copy}
                kind="blank"
                inverted
                iconOnly
                leadingIcon={Icon.Copy}
                size="small"
                onPress={() => {
                    clipboard.writeText(resolved.href);
                }}
                preventFocusOnPress
            />
            <Button
                label={actionsLabels[kind].remove}
                kind="blank"
                inverted
                iconOnly
                leadingIcon={Icon.Trashcan}
                size="small"
                onPress={onPressRemove}
                preventFocusOnPress
            />
        </div>
    );
}

const actionsLabels = {
    link: {
        open: 'Open link',
        edit: 'Edit link',
        copy: 'Copy link',
        remove: 'Remove link',
    },
    reference: {
        open: 'Open reference',
        edit: 'Edit reference',
        copy: 'Copy link to reference',
        remove: 'Remove reference',
    },
};
